"use client";

import { useEffect, useRef } from "react";

/**
 * Cal.com inline booking embed (namespace "consultoria-fitness",
 * calLink "avantefit/consultoria-fitness", month_view), reproducing the
 * original WordPress embed.
 *
 * Robustness:
 * - The official Cal bootstrap self-guards its <script> injection
 *   (`if (!cal.loaded)`), so navigating back to this route never loads
 *   embed.js twice.
 * - A ref guards against re-running the `inline`/`ui` calls within a single
 *   mount (React StrictMode double-invoke), and the cleanup empties the
 *   container + resets the guard so an App Router remount re-initializes
 *   cleanly instead of stacking duplicate calendars.
 */
const EMBED_JS = "https://app.cal.com/embed/embed.js";
const NAMESPACE = "consultoria-fitness";
const CAL_LINK = "avantefit/consultoria-fitness";
const ELEMENT_ID = "my-cal-inline-consultoria-fitness";

export function CalcomEmbed() {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (function (C: any, A: string, L: string) {
      const p = function (a: any, ar: any) {
        a.q.push(ar);
      };
      const d = C.document;
      C.Cal =
        C.Cal ||
        function () {
          const cal = C.Cal;
          const ar = arguments;
          if (!cal.loaded) {
            cal.ns = {};
            cal.q = cal.q || [];
            d.head.appendChild(d.createElement("script")).src = A;
            cal.loaded = true;
          }
          if (ar[0] === L) {
            const api = function () {
              p(api, arguments);
            };
            const namespace = ar[1];
            (api as any).q = (api as any).q || [];
            if (typeof namespace === "string") {
              cal.ns[namespace] = cal.ns[namespace] || api;
              p(cal.ns[namespace], ar);
              p(cal, ["initNamespace", namespace]);
            } else p(cal, ar);
            return;
          }
          p(cal, ar);
        };
    })(window, EMBED_JS, "init");

    const Cal = (window as any).Cal;
    Cal("init", NAMESPACE, { origin: "https://app.cal.com" });
    Cal.ns[NAMESPACE]("inline", {
      elementOrSelector: `#${ELEMENT_ID}`,
      config: { layout: "month_view" },
      calLink: CAL_LINK,
    });
    Cal.ns[NAMESPACE]("ui", {
      hideEventTypeDetails: false,
      layout: "month_view",
    });

    return () => {
      const el = document.getElementById(ELEMENT_ID);
      if (el) el.innerHTML = "";
      initialized.current = false;
    };
  }, []);

  return (
    <div
      style={{ width: "100%", height: "100%", overflow: "scroll" }}
      id={ELEMENT_ID}
    />
  );
}
